import React, { useState, useEffect } from 'react';

// === DUMMY DATA ===
const departmentsData = [
  { id: 1, name: 'Dental Care', icon: '🦷', desc: 'Advanced root canals, implants, and cosmetic dentistry with painless technology.' },
  { id: 2, name: 'Cardiology', icon: '❤️', desc: 'Comprehensive heart care, ECG, and preventive cardiology.' },
  { id: 3, name: 'Pediatrics', icon: '👶', desc: 'Expert child healthcare, vaccinations, and neonatal care in a friendly environment.' },
  { id: 4, name: 'Dermatology', icon: '✨', desc: 'Skin, hair, and nail treatments, including laser therapies and acne solutions.' },
  { id: 5, name: 'Orthopedics', icon: '🦴', desc: 'Treatment for joint pains, fractures, sports injuries, and arthritis.' },
  { id: 6, name: 'General Medicine', icon: '🩺', desc: 'Primary care, viral fevers, diabetes management, and routine checkups.' }
];

const doctorsData = [
  { name: 'Dr. Rajesh Sharma', role: 'Head of Dental / Endodontist', exp: '15+ Yrs Exp.', img: 'https://drrajeshgupta.com/wp-content/uploads/2024/04/MG_8228-1-scaled-e1716026571390.jpg' },
  { name: 'Dr. Ananya Gupta', role: 'Senior Dermatologist', exp: '10+ Yrs Exp.', img: 'https://kotharimedical.com/wp-content/uploads/2023/10/dr-ananya-bhattacharya-65b0c27c6f1d9.webp' },
  { name: 'Dr. Vikram Singh', role: 'Cardiologist (MD, DM)', exp: '18+ Yrs Exp.', img: 'https://admin.asterclinic.ae:3000/uploads/doctor/1754892471571-Untitled_design21_optimized.png' }
];

const reviewsData = [
  { name: 'Sanjay Kumar', rating: '⭐⭐⭐⭐⭐', text: 'Dr. Sharma is brilliant. I had a painless root canal and the clinic hygiene is top-notch. Highly recommended for families.' },
  { name: 'Priya Verma', rating: '⭐⭐⭐⭐⭐', text: 'Brought my 3-year-old here for fever. The pediatric department is so welcoming, and the doctor was very patient.' },
  { name: 'Amit Desai', rating: '⭐⭐⭐⭐', text: 'Very professional setup. I booked my appointment online, and there was zero waiting time when I arrived.' }
];

const faqsData = [
  { q: 'Do you accept health insurance?', a: 'Yes, we are empaneled with all major health insurance providers (Star, HDFC Ergo, ICICI Lombard) for cashless claims.' },
  { q: 'Is prior appointment mandatory?', a: 'While walk-ins are welcome, we strongly advise booking an appointment online or via phone to avoid waiting.' },
  { q: 'Do you have an in-house pharmacy and lab?', a: 'Yes, we have a fully equipped diagnostic lab and a 24/7 pharmacy on the ground floor.' }
];

// === COMPONENT ===
const ClinicMidTier: React.FC = () => {
  type Page = 'home' | 'departments' | 'doctors' | 'reviews' | 'contact';
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, [currentPage]);

  const handleBooking = (e: React.FormEvent) => {
    e.preventDefault();
    setIsBooked(true);
    setTimeout(() => setIsBooked(false), 5000);
  };

  // Reusable Page Header
  const PageHeader = ({ title, subtitle }: { title: string, subtitle: string }) => (
    <div className="bg-teal-800 text-white py-16 px-6 text-center">
      <div className="max-w-3xl mx-auto animate-slideUp">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
        <p className="text-teal-100 text-lg">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans flex flex-col selection:bg-teal-200">
      
      {/* --- TOP EMERGENCY BAR --- */}
      <div className="bg-slate-900 text-white text-xs sm:text-sm py-2.5 px-6 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-4 text-slate-300">
          <span className="flex items-center gap-2">🕒 Mon-Sat: 9AM - 8PM (Sun: Emergency Only)</span>
        </div>
        <div className="font-bold tracking-wider uppercase text-teal-400 flex items-center gap-2">
          <span className="animate-pulse">🚨</span> Emergency Hotline: <a href="tel:+919999988888" className="text-white hover:text-teal-300 transition">+91 99999 88888</a>
        </div>
      </div>

      {/* --- MAIN NAVBAR --- */}
      <nav className="bg-white sticky top-0 z-50 shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer group" onClick={() => setCurrentPage('home')}>
            <div className="w-10 h-10 bg-teal-600 text-white flex items-center justify-center rounded-lg text-2xl font-bold group-hover:bg-teal-700 transition">✚</div>
            <div className="leading-tight">
              <span className="text-2xl font-bold text-slate-900 tracking-tight block">ApexCare</span>
              <span className="text-[10px] uppercase tracking-widest text-teal-600 font-bold block">Polyclinic & Diagnostics</span>
            </div>
          </div>
          
          {/* Desktop Links */}
          <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wider text-slate-500">
            {['home', 'departments', 'doctors', 'reviews', 'contact'].map(page => (
              <button 
                key={page} 
                onClick={() => setCurrentPage(page as Page)}
                className={`hover:text-teal-600 transition ${currentPage === page ? 'text-teal-700 border-b-2 border-teal-600 pb-1' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => setCurrentPage('contact')} className="bg-teal-600 text-white px-5 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-teal-700 transition shadow-lg hidden sm:block">
              Book Visit
            </button>
            {/* Mobile Hamburger */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-teal-600 text-3xl leading-none">
              ☰
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-200 shadow-xl flex flex-col p-6 animate-fadeIn">
            {['home', 'departments', 'doctors', 'reviews', 'contact'].map(page => (
              <button 
                key={page} 
                onClick={() => setCurrentPage(page as Page)}
                className={`py-3 text-left text-sm font-bold uppercase tracking-wider border-b border-slate-100 transition ${currentPage === page ? 'text-teal-600' : 'text-slate-500'}`}
              >
                {page}
              </button>
            ))}
            <button onClick={() => setCurrentPage('contact')} className="mt-6 w-full bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-teal-700 transition">
              Book Appointment
            </button>
          </div>
        )}
      </nav>

      {/* --- PAGE CONTENT AREA --- */}
      <main className="flex-grow animate-fadeIn">
        
        {/* ================= 1. HOME PAGE ================= */}
        {currentPage === 'home' && (
          <div className="animate-slideUp">
            
            {/* Hero Section */}
            <header className="bg-white px-6 py-16 md:py-24 border-b border-slate-100 overflow-hidden">
              <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center relative">
                <div className="space-y-6 md:pr-8 relative z-10">
                  <span className="inline-block bg-teal-50 text-teal-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border border-teal-100">NABL Accredited Facility</span>
                  <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight">
                    Advanced Healthcare, <br/> <span className="text-teal-600">Personalized</span> For You.
                  </h1>
                  <p className="text-lg text-slate-500 leading-relaxed">
                    From routine dental checkups to specialized cardiology, ApexCare brings top-tier doctors and state-of-the-art diagnostics under one roof for your family's complete well-being.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button onClick={() => setCurrentPage('contact')} className="bg-teal-600 text-white text-center px-8 py-4 rounded-xl font-bold hover:bg-teal-700 transition shadow-xl shadow-teal-600/20">
                      Book Consultation
                    </button>
                    <button onClick={() => setCurrentPage('departments')} className="bg-white text-slate-700 border-2 border-slate-200 text-center px-8 py-4 rounded-xl font-bold hover:border-teal-600 hover:text-teal-700 transition">
                      Explore Specialties
                    </button>
                  </div>
                </div>
                <div className="relative h-[400px] md:h-[550px] rounded-3xl overflow-hidden shadow-2xl border-8 border-white z-10">
                  <img src="https://pkonomist.com/wp-content/uploads/2024/10/Future-Prospects-of-AI-in-Healthcare.jpg" alt="Hospital Reception" className="w-full h-full object-cover"/>
                </div>
                {/* Decorative blob */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-50 rounded-full blur-3xl -z-0 opacity-50"></div>
              </div>
            </header>

            {/* Quick Stats Strip */}
            <div className="bg-teal-800 text-white py-12 px-6">
              <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-teal-700">
                <div><h4 className="text-4xl font-bold text-teal-300 mb-1">15+</h4><p className="text-xs uppercase tracking-widest text-teal-100">Specialists</p></div>
                <div><h4 className="text-4xl font-bold text-teal-300 mb-1">50k+</h4><p className="text-xs uppercase tracking-widest text-teal-100">Happy Patients</p></div>
                <div><h4 className="text-4xl font-bold text-teal-300 mb-1">24/7</h4><p className="text-xs uppercase tracking-widest text-teal-100">Pharmacy & Lab</p></div>
                <div><h4 className="text-4xl font-bold text-teal-300 mb-1">100%</h4><p className="text-xs uppercase tracking-widest text-teal-100">Hygiene Focus</p></div>
              </div>
            </div>

            {/* Why Choose Us */}
            <section className="py-24 px-6 max-w-7xl mx-auto text-center">
              <span className="text-teal-600 font-bold uppercase tracking-widest text-sm">Our Promise</span>
              <h2 className="text-4xl font-bold text-slate-900 mt-2 mb-16">Why Choose ApexCare?</h2>
              <div className="grid md:grid-cols-3 gap-10">
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition">
                  <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">🩺</div>
                  <h3 className="text-xl font-bold mb-3">Multi-Disciplinary Team</h3>
                  <p className="text-slate-500">A panel of highly qualified doctors collaborating to provide comprehensive care plans.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition">
                  <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">🔬</div>
                  <h3 className="text-xl font-bold mb-3">Modern Diagnostics</h3>
                  <p className="text-slate-500">In-house digital X-rays, ECG, and pathology lab for fast and accurate reporting.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition">
                  <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">💳</div>
                  <h3 className="text-xl font-bold mb-3">Transparent Pricing</h3>
                  <p className="text-slate-500">Ethical practice with no hidden costs. Cashless insurance facilities available.</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ================= 2. DEPARTMENTS PAGE ================= */}
        {currentPage === 'departments' && (
          <div className="animate-slideUp pb-24">
            <PageHeader title="Centers of Excellence" subtitle="Comprehensive care across major medical specialties." />
            <div className="max-w-7xl mx-auto px-6 mt-16">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {departmentsData.map(dept => (
                  <div key={dept.id} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:border-teal-400 hover:shadow-xl transition group">
                    <span className="text-5xl mb-6 block group-hover:-translate-y-2 transition-transform">{dept.icon}</span>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{dept.name}</h3>
                    <p className="text-slate-500 leading-relaxed mb-6">{dept.desc}</p>
                    <button onClick={() => setCurrentPage('contact')} className="text-teal-600 font-bold uppercase tracking-widest text-xs hover:text-teal-800 transition flex items-center gap-2">
                      Consult Specialist <span>➔</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 3. DOCTORS PAGE ================= */}
        {currentPage === 'doctors' && (
          <div className="animate-slideUp pb-24">
            <PageHeader title="Meet Our Specialists" subtitle="Highly experienced professionals dedicated to your healing." />
            <div className="max-w-7xl mx-auto px-6 mt-16">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {doctorsData.map((doc, idx) => (
                  <div key={idx} className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 group">
                    <div className="h-72 overflow-hidden relative bg-slate-100">
                      <img src={doc.img} alt={doc.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700 grayscale group-hover:grayscale-0"/>
                      <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-lg text-xs font-bold text-slate-700 shadow-sm">
                        {doc.exp}
                      </div>
                    </div>
                    <div className="p-8 text-center">
                      <h3 className="text-2xl font-bold text-slate-900 mb-1">{doc.name}</h3>
                      <p className="text-teal-600 font-bold uppercase tracking-widest text-xs mb-6">{doc.role}</p>
                      <button onClick={() => setCurrentPage('contact')} className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-3 rounded-xl font-bold hover:bg-teal-600 hover:text-white hover:border-teal-600 transition">
                        Book Appointment
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-16 bg-teal-50 p-10 rounded-3xl text-center border border-teal-100">
                 <h3 className="text-2xl font-bold text-slate-900 mb-4">Looking for a specific specialist?</h3>
                 <p className="text-slate-600 mb-6">Call our front desk, and we will guide you to the right department.</p>
                 <a href="tel:+919876543210" className="inline-block bg-teal-700 text-white font-bold px-8 py-3 rounded-xl hover:bg-teal-800 transition shadow-lg">📞 Call +91 98765 43210</a>
              </div>
            </div>
          </div>
        )}

        {/* ================= 4. REVIEWS PAGE ================= */}
        {currentPage === 'reviews' && (
          <div className="animate-slideUp pb-24 bg-slate-50 min-h-screen">
            <PageHeader title="Patient Stories" subtitle="Read what our patients have to say about their experience." />
            <div className="max-w-7xl mx-auto px-6 mt-16">
              <div className="grid md:grid-cols-3 gap-8">
                {reviewsData.map((review, idx) => (
                  <div key={idx} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200">
                    <div className="text-lg mb-4">{review.rating}</div>
                    <p className="text-slate-600 italic leading-relaxed mb-6">"{review.text}"</p>
                    <div className="flex items-center gap-4 pt-6 border-t border-slate-100">
                      <div className="w-12 h-12 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold text-xl">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{review.name}</p>
                        <p className="text-xs text-slate-400 uppercase tracking-widest">Verified Patient</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Write a review CTA */}
              <div className="mt-20 text-center">
                 <h3 className="text-3xl font-bold text-slate-900 mb-4">Your Feedback Matters</h3>
                 <p className="text-slate-500 mb-8 max-w-2xl mx-auto">We constantly strive to improve our services. If you've visited us recently, please leave a review on our Google Business Profile.</p>
                 <button className="bg-blue-600 text-white font-bold px-8 py-4 rounded-xl hover:bg-blue-700 transition shadow-lg flex items-center gap-3 mx-auto">
                   <span className="text-xl">G</span> Review on Google
                 </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= 5. CONTACT & BOOKING PAGE ================= */}
        {currentPage === 'contact' && (
          <div className="animate-slideUp py-16 md:py-24 px-6 max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-16">
              
              {/* Form Side */}
              <div className="lg:col-span-7 bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-slate-100">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Book Your Visit</h2>
                <p className="text-slate-500 mb-10">Fill out the form below and we will confirm your appointment.</p>
                
                {isBooked ? (
                  <div className="bg-teal-50 border border-teal-200 text-teal-800 p-10 rounded-2xl text-center h-[400px] flex flex-col justify-center items-center">
                    <span className="text-6xl mb-6 block">✅</span>
                    <h3 className="text-2xl font-bold mb-2">Request Received!</h3>
                    <p className="text-teal-600">Our reception team will call you within 15 minutes to confirm the doctor's availability and your time slot.</p>
                    <button onClick={() => setIsBooked(false)} className="mt-8 text-sm font-bold underline hover:text-teal-900">Book another slot</button>
                  </div>
                ) : (
                  <form onSubmit={handleBooking} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Patient Name</label>
                        <input required type="text" className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition" placeholder="John Doe" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Phone Number</label>
                        <input required type="tel" className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition" placeholder="+91 00000 00000" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Select Department</label>
                        <select className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:border-teal-500 outline-none transition text-slate-700">
                          <option>Dental Care</option><option>Cardiology</option><option>Pediatrics</option><option>Dermatology</option><option>General Consult</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Preferred Date</label>
                        <input required type="date" className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:border-teal-500 outline-none transition text-slate-700" />
                      </div>
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Briefly describe your issue (Optional)</label>
                       <textarea rows={3} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl focus:border-teal-500 outline-none transition resize-none" placeholder="E.g., Severe toothache, routine checkup..."></textarea>
                    </div>
                    <button type="submit" className="w-full bg-teal-600 text-white font-bold uppercase tracking-widest py-5 rounded-xl hover:bg-teal-700 transition shadow-xl mt-4 text-sm">
                      Submit Request
                    </button>
                  </form>
                )}
              </div>

              {/* Info & FAQ Side */}
              <div className="lg:col-span-5 space-y-12 flex flex-col justify-center">
                
                {/* Contact Cards */}
                <div className="space-y-6">
                  <div className="flex gap-5 items-start p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <span className="text-3xl text-teal-600 mt-1">📍</span>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg mb-1">Clinic Address</h4>
                      <p className="text-slate-600 leading-relaxed">Ground Floor, Health Square,<br/>Sector 14, City Center, 110001</p>
                    </div>
                  </div>
                  <div className="flex gap-5 items-start p-6 bg-teal-50 rounded-2xl border border-teal-100 shadow-sm">
                    <span className="text-3xl text-teal-600 mt-1">📞</span>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg mb-1">Call Us Directly</h4>
                      <p className="text-slate-600">Appointments: +91 98765 43210</p>
                      <p className="text-red-600 font-bold mt-1">Emergency: +91 99999 88888</p>
                    </div>
                  </div>
                </div>

                {/* FAQ Accordion Concept */}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked</h3>
                  <div className="space-y-4">
                    {faqsData.map((faq, idx) => (
                      <div key={idx} className="border-b border-slate-200 pb-4">
                        <h4 className="font-bold text-slate-800 text-sm mb-2 flex items-start gap-2">
                          <span className="text-teal-600">Q.</span> {faq.q}
                        </h4>
                        <p className="text-slate-500 text-sm leading-relaxed pl-6">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

      </main>

      {/* --- FOOTER --- */}
      <footer className="bg-slate-900 text-slate-400 py-16 border-t-[6px] border-teal-600 mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-600 text-white flex items-center justify-center rounded text-xl font-bold">✚</span>
              <span className="text-xl font-bold text-white tracking-tight">ApexCare</span>
            </div>
            <p className="text-sm leading-relaxed">Providing compassionate, comprehensive, and advanced healthcare to families. Your health is our priority.</p>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => setCurrentPage('home')} className="hover:text-teal-400 transition">Home</button></li>
              <li><button onClick={() => setCurrentPage('departments')} className="hover:text-teal-400 transition">Departments</button></li>
              <li><button onClick={() => setCurrentPage('doctors')} className="hover:text-teal-400 transition">Our Doctors</button></li>
              <li><button onClick={() => setCurrentPage('contact')} className="hover:text-teal-400 transition">Contact Us</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Departments</h4>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => setCurrentPage('departments')} className="hover:text-teal-400 transition">Dental Care</button></li>
              <li><button onClick={() => setCurrentPage('departments')} className="hover:text-teal-400 transition">Cardiology</button></li>
              <li><button onClick={() => setCurrentPage('departments')} className="hover:text-teal-400 transition">Pediatrics</button></li>
              <li><button onClick={() => setCurrentPage('departments')} className="hover:text-teal-400 transition">Dermatology</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Legal & Emergency</h4>
            <ul className="space-y-3 text-sm">
              <li className="text-red-400 font-bold uppercase">Ambulance: 108</li>
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms & Conditions</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto border-t border-slate-800 pt-8 px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-bold tracking-widest uppercase">
          <p>© {new Date().getFullYear()} ApexCare Multispecialty Clinic.</p>
          <p className="text-teal-700">Premium Portfolio Demo</p>
        </div>
      </footer>

      {/* Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
      `}} />
    </div>
  );
};

export default ClinicMidTier;